import styled from "styled-components";
import { useState, useMemo, useCallback } from "react";
import { usePage } from "@inertiajs/react";
import {
  Wifi,
  Waves,
  Plus,
  Minus,
  Maximize,
  Loader2,
  KeyRound,
  Info,
  Clock,
  Check,
} from "lucide-react";

import RoomGallery from "../_kit/RoomGallery";
import Lightbox from "./Lightbox";
import DateRangePicker from "../_kit/DateRangePicker";
import Card from "../_kit/Card";
import Button from "../_kit/Button";
import {
  Section,
  SectionHeader,
  Lede,
  Heading,
  Eyebrow,
  Container,
} from "../_kit/Section";
import { useLandingLanguage } from "../../../contexts/landing-language";
import { addThousandSeparator } from "../../../util";
import {
  weight,
  type,
  tracking,
  space,
  shadow,
  radius,
  media,
  leading,
  font,
  color,
} from "../../../styles/tokens";

/* The booking surface.
 *
 * Rates and availability are fetched per date range rather than shipped with the page: a guest
 * changes dates several times before committing, and re-rendering the page for each change would
 * throw away the rooms they have already chosen.
 *
 * Booking ends at WhatsApp for now. Everything assembled here — dates, rooms, guest counts, the
 * derived extra beds and the estimate — is folded into one pre-filled message so the front desk
 * receives a complete request instead of starting a conversation from nothing.
 */

const ENDPOINT = "/hebe/availability";

const rupiah = (value) =>
  `Rp ${addThousandSeparator(String(Math.round(value)))}`;

const DateBar = styled.form`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${space[4]};
  align-items: end;
  padding: ${space[5]};
  background: ${color.surface};
  border: 1px solid ${color.border};
  border-radius: ${radius.md};
  box-shadow: ${shadow.md};

  ${media.sm`
    grid-template-columns: 1fr;
  `}
`;

const Nights = styled.p`
  grid-column: 1 / -1;
  margin: 0;
  font-size: ${type.sm};
  color: ${color.ink500};
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: ${space[6]};
  align-items: start;
  margin-top: ${space[7]};

  ${media.lg`
    grid-template-columns: 1fr;
  `}
`;

const RoomCard = styled(Card)`
  /* image | detail | a fixed control rail.
   *
   * Two columns left the right half of every card empty while the content crowded into the middle,
   * and the steppers sat in a wrapping flex row where only some had hint text — so they landed on
   * three different baselines. The rail gives them one column and one rhythm. */
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr) 272px;
  gap: 0;
  margin-bottom: ${space[4]};

  /* Collapses at 1180px, not at the shared lg breakpoint: between 1024 and ~1150 the three tracks
   * squeezed the detail column to about 144px, which is narrower than the room name. */
  @media (max-width: 1180px) {
    grid-template-columns: 220px minmax(0, 1fr);
  }

  ${media.md`
    grid-template-columns: 1fr;
  `}
`;

const RoomBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[2]};
  padding: ${space[5]};
`;

const RoomName = styled.h3`
  margin: 0;
  font-family: ${font.display};
  font-size: ${type.h3};
  font-weight: ${weight.regular};
  color: ${color.ink};
`;

const Price = styled.div`
  padding-bottom: ${space[3]};
  border-bottom: 1px solid ${color.border};

  strong {
    display: block;
    font-family: ${font.display};
    font-size: ${type.h3};
    font-weight: ${weight.semibold};
    color: ${color.ink};
  }

  small {
    font-size: ${type.xs};
    letter-spacing: ${tracking.eyebrow};
    text-transform: uppercase;
    color: ${color.ink500};
  }
`;

const RoomDesc = styled.p`
  margin: 0;
  font-size: ${type.sm};
  line-height: ${leading.relaxed};
  color: ${color.ink500};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

/* Facts a guest actually decides on. The middle column carried a name and a badge and nothing
 * else, which is most of why the card read as empty. */
const Facts = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${space[4]};
  margin: auto 0 0;
  padding: 0;
  list-style: none;
  font-size: ${type.sm};
  color: ${color.ink700};

  li {
    display: inline-flex;
    align-items: center;
    gap: 7px;
  }
`;

const Badge = styled.span`
  align-self: flex-start;
  padding: 4px 10px;
  border-radius: ${radius.pill};
  font-size: ${type.xs};
  font-weight: ${weight.semibold};
  letter-spacing: ${tracking.wide};
  text-transform: uppercase;
  background: ${(props) =>
    props.$state === "sold_out"
      ? "#F2E7E5"
      : props.$state === "limited"
        ? color.goldSoft
        : color.primarySoft};
  color: ${(props) =>
    props.$state === "sold_out"
      ? "#8A3A2C"
      : props.$state === "limited"
        ? color.goldText
        : color.primaryText};
`;

const Rail = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};
  padding: ${space[5]};
  border-left: 1px solid ${color.border};
  background: ${color.canvas};

  ${media.lg`
    grid-column: 1 / -1;
    border-left: 0;
    border-top: 1px solid ${color.border};
  `}
`;

const Counters = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Counter = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: ${space[3]};
  padding: 8px 0;

  & + & {
    border-top: 1px solid ${color.border};
  }

  span {
    font-size: ${type.sm};
    font-weight: ${weight.medium};
    color: ${color.ink};
  }

  small {
    display: block;
    margin-top: 1px;
    font-size: ${type.xs};
    line-height: 1.35;
    color: ${color.ink500};
  }
`;

/* The label column, so the hint sits under its label instead of pushing the stepper down. */
const CounterLabel = styled.div`
  min-width: 0;
`;

const Stepper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border: 1px solid ${color.border};
  border-radius: ${radius.pill};
  overflow: hidden;

  button {
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border: 0;
    background: none;
    color: ${color.ink};
    cursor: pointer;

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }

  output {
    min-width: 26px;
    text-align: center;
    font-size: ${type.base};
    font-weight: ${weight.semibold};
  }
`;

const BedNote = styled.p`
  margin: 0;
  font-size: ${type.xs};
  color: ${color.ink500};
`;

const Basket = styled.aside`
  position: sticky;
  top: 130px;
  padding: ${space[5]};
  background: ${color.surface};
  border: 1px solid ${color.border};
  border-radius: ${radius.md};

  ${media.lg`
    position: static;
  `}
`;

const BasketTitle = styled.h3`
  margin: 0 0 ${space[4]};
  font-family: ${font.display};
  font-size: ${type.h4};
  font-weight: ${weight.semibold};
  color: ${color.ink};
`;

const Line = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${space[3]};
  padding: 10px 0;
  border-bottom: 1px solid ${color.border};
  font-size: ${type.sm};
  color: ${color.ink700};

  strong {
    white-space: nowrap;
  }
`;

const Total = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin: ${space[4]} 0;
  font-family: ${font.display};

  span {
    font-size: ${type.sm};
    font-family: ${font.body};
    color: ${color.ink500};
  }

  strong {
    font-size: ${type.h3};
    font-weight: ${weight.semibold};
    color: ${color.ink};
  }
`;

const Note = styled.p`
  display: flex;
  gap: 8px;
  margin: ${space[5]} 0 0;
  font-size: ${type.xs};
  line-height: ${leading.relaxed};
  color: ${color.ink500};

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

/* Sits under the basket, filling the column that was empty below it and answering the question a
 * guest is actually weighing at that moment: what am I getting for this. */
const Included = styled.div`
  margin-top: ${space[4]};
  padding: ${space[5]};
  border: 1px solid ${color.border};
  border-radius: ${radius.md};
  background: ${color.primarySoft};

  h4 {
    margin: 0 0 ${space[3]};
    font-family: ${font.display};
    font-size: ${type.h4};
    font-weight: ${weight.semibold};
    color: ${color.primaryText};
  }

  ul {
    display: flex;
    flex-direction: column;
    gap: 9px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    font-size: ${type.sm};
    line-height: ${leading.normal};
    color: ${color.ink700};
  }

  svg {
    flex-shrink: 0;
    margin-top: 3px;
    color: ${color.primary};
  }
`;

/* Stay times, under the date bar because that is the moment a guest is reasoning about days.
 * Values come from the CMS nap block, NOT AppSetting::CHECK_IN_WINDOW_START — that one is the
 * kiosk self-check-in window (12:00), not the advertised policy, and publishing it would tell
 * guests they can arrive two hours before they actually can. */
const StayTimes = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${space[3]} ${space[6]};
  margin-top: ${space[4]};
  padding: ${space[4]} ${space[5]};
  border: 1px solid ${color.border};
  border-radius: ${radius.md};
  background: ${color.surface};
`;

const StayItem = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: ${type.sm};
  color: ${color.ink};

  svg {
    flex-shrink: 0;
    color: ${color.primary};
  }
`;

const StayNote = styled.p`
  flex-basis: 100%;
  margin: 0;
  font-size: ${type.xs};
  line-height: ${leading.relaxed};
  color: ${color.ink500};

  strong {
    font-weight: ${weight.semibold};
    color: ${color.ink700};
  }
`;

const Empty = styled.p`
  margin: 0;
  padding: ${space[6]} 0;
  text-align: center;
  font-size: ${type.sm};
  color: ${color.ink500};
`;

const Asterisk = styled.sup`
  color: ${color.goldText};
`;

const BookingPanel = (props) => {
  const {
    room_types: roomTypes,
    booking,
    landing_content: landingContent,
  } = usePage().props;
  const { t, language } = useLandingLanguage();
  const copy = t.booking;
  const policy = booking?.policy || {};
  const included = policy.guests_included_per_room || 2;
  const maxGuests = policy.max_guests_per_room || 4;

  // Seeded from the server so SSR and hydration agree, and so the floor is the property's today.
  const today = booking?.today || booking?.default_check_in;
  const [checkIn, setCheckIn] = useState(booking?.default_check_in || "");
  const [checkOut, setCheckOut] = useState(booking?.default_check_out || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // { [code]: { rooms, guests, children } }
  const [picked, setPicked] = useState({});
  // Which room's photos are open full-screen, and at which one.
  const [viewing, setViewing] = useState(null);

  // CMS content keyed by the code that joins it to the application database.
  const contentByCode = useMemo(() => {
    const map = {};
    (roomTypes || []).forEach((room) => {
      if (room.room_type_code) map[room.room_type_code] = room;
    });
    return map;
  }, [roomTypes]);

  const search = useCallback(
    async (event) => {
      event?.preventDefault();
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `${ENDPOINT}?check_in=${checkIn}&check_out=${checkOut}`,
          {
            headers: { Accept: "application/json" },
          },
        );
        const body = await res.json();
        if (!res.ok) throw new Error((body.errors || []).join(", "));
        setData(body);
        setPicked({});
      } catch (caught) {
        setError(caught.message);
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [checkIn, checkOut],
  );

  const setPick = (code, patch) =>
    setPicked((current) => {
      const existing = current[code] || {
        rooms: 0,
        guests: included,
        children: 0,
      };
      const next = { ...existing, ...patch };
      if (next.rooms <= 0) {
        const copyState = { ...current };
        delete copyState[code];
        return copyState;
      }
      return { ...current, [code]: next };
    });

  /* Extra beds are DERIVED, never chosen: a guest knows how many people are coming, not how many
   * beds that implies. Deriving it also makes an impossible combination — four guests and no extra
   * bed — unrepresentable. Young children are excluded because they are free. */
  const extraBedsFor = useCallback(
    (guests) => Math.max(0, Math.min(guests, maxGuests) - included),
    [maxGuests, included],
  );

  const lines = useMemo(() => {
    if (!data) return [];
    return Object.entries(picked).map(([code, pick]) => {
      const offer = data.room_types.find((room) => room.code === code);
      const beds = extraBedsFor(pick.guests) * pick.rooms;
      const bedTotal =
        beds * (data.extra_bed?.per_night_rupiah || 0) * data.nights;
      const roomTotal = (offer?.total_rupiah || 0) * pick.rooms;
      return {
        code,
        name: offer?.name || code,
        rooms: pick.rooms,
        guests: pick.guests,
        children: pick.children,
        beds,
        total: roomTotal + bedTotal,
      };
    });
  }, [picked, data, extraBedsFor]);

  const grandTotal = lines.reduce((sum, line) => sum + line.total, 0);

  const whatsappHref = useMemo(() => {
    if (!booking?.whatsapp || !data || lines.length === 0) return null;
    const isId = language === "id";
    const rows = lines.map(
      (line) =>
        `• ${line.rooms}x ${line.name} — ${line.guests} ${isId ? "tamu" : "guests"}` +
        (line.children
          ? ` + ${line.children} ${isId ? "anak" : "children"}`
          : "") +
        (line.beds ? ` (${line.beds} extra bed)` : ""),
    );
    const text = [
      isId
        ? "Halo Hebe, saya mau booking:"
        : "Hello Hebe, I would like to book:",
      "",
      `${isId ? "Tanggal" : "Dates"}: ${data.check_in} → ${data.check_out} (${data.nights} ${isId ? "malam" : data.nights === 1 ? "night" : "nights"})`,
      ...rows,
      "",
      `${isId ? "Perkiraan total" : "Estimated total"}: ${rupiah(grandTotal)}`,
      isId
        ? "(Saya mengerti tarif dapat berubah sesuai ketentuan properti.)"
        : "(I understand the rate may change according to the property's rules.)",
    ].join("\n");
    return `https://wa.me/${booking.whatsapp}?text=${encodeURIComponent(text)}`;
  }, [booking, data, lines, grandTotal, language]);

  // The property's advertised policy, editable in the CMS; the defaults match the schema's.
  const nap = landingContent?.nap || {};
  const checkInTime = nap.checkin_time_display || "14:00";
  const checkOutTime = nap.checkout_time_display || "12:00";

  const nights = data?.nights;

  return (
    <Section id="booking">
      <Container>
        <SectionHeader>
          <Eyebrow>{copy.eyebrow}</Eyebrow>
          <Heading>{copy.heading}</Heading>
          <Lede>{copy.extraBedNote}</Lede>
        </SectionHeader>

        <DateBar onSubmit={search}>
          <DateRangePicker
            checkIn={checkIn}
            checkOut={checkOut}
            today={today}
            maxNights={booking?.max_nights}
            language={language}
            labels={copy}
            onChange={(range) => {
              setCheckIn(range.checkIn || "");
              setCheckOut(range.checkOut || "");
            }}
          />
          <Button
            type="submit"
            $variant="primary"
            $size="lg"
            disabled={loading || !checkIn || !checkOut}
          >
            {loading ? <Loader2 size={16} aria-hidden="true" /> : null}
            {loading ? copy.searching : copy.search}
          </Button>
          {nights ? (
            <Nights>
              {nights} {nights === 1 ? copy.night : copy.nights}
            </Nights>
          ) : null}
        </DateBar>

        <StayTimes>
          <StayItem>
            <Clock size={15} aria-hidden="true" />
            {copy.checkInFrom.replace("%{t}", checkInTime)}
          </StayItem>
          <StayItem>
            <Clock size={15} aria-hidden="true" />
            {copy.checkOutBy.replace("%{t}", checkOutTime)}
          </StayItem>
          <StayNote>
            <strong>{copy.earlyCheckIn}:</strong> {copy.earlyCheckInNote}
          </StayNote>
        </StayTimes>

        {error ? <Empty role="alert">{error}</Empty> : null}
        {!data && !loading && !error ? <Empty>{copy.pickDates}</Empty> : null}

        {data ? (
          <Layout>
            <div>
              {data.room_types.length === 0 ? (
                <Empty>{copy.noRooms}</Empty>
              ) : null}
              {data.room_types.map((offer) => {
                const content = contentByCode[offer.code];
                const pick = picked[offer.code];
                const soldOut = offer.availability === "sold_out";
                const beds = pick ? extraBedsFor(pick.guests) : 0;

                return (
                  <RoomCard key={offer.code}>
                    <RoomGallery
                      images={content?.images || []}
                      alt={content?.name || offer.name}
                      sizes="(max-width: 768px) 100vw, 260px"
                      photosLabel={copy.photos}
                      viewLabel={copy.viewPhotos}
                      onOpen={(at) =>
                        setViewing({
                          images: content?.images || [],
                          at,
                          name: content?.name || offer.name,
                        })
                      }
                    />

                    <RoomBody>
                      <RoomName>{content?.name || offer.name}</RoomName>
                      <Badge $state={offer.availability}>
                        {offer.availability === "available"
                          ? copy.available
                          : offer.availability === "limited"
                            ? copy.limited
                            : copy.soldOut}
                      </Badge>
                      {content?.short_desc ? (
                        <RoomDesc>{content.short_desc}</RoomDesc>
                      ) : null}
                      {/* What comes with the room. Capacity is not listed here — the guest stepper
                       * already enforces and explains it, so repeating it was noise. */}
                      <Facts>
                        {content?.luas_m2 ? (
                          <li>
                            <Maximize size={15} aria-hidden="true" />
                            {copy.roomSize.replace("%{n}", content.luas_m2)}
                          </li>
                        ) : null}
                        <li>
                          <Wifi size={15} aria-hidden="true" />
                          {copy.freeWifi}
                        </li>
                        <li>
                          <Waves size={15} aria-hidden="true" />
                          {copy.poolAccess}
                        </li>
                        <li>
                          <KeyRound size={15} aria-hidden="true" />
                          {copy.selfCheckIn}
                        </li>
                      </Facts>
                    </RoomBody>

                    <Rail>
                      <Price>
                        <strong>
                          {rupiah(offer.per_night_rupiah)}
                          <Asterisk>*</Asterisk>
                        </strong>
                        <small>{copy.perNight}</small>
                      </Price>

                      {soldOut ? null : (
                        <>
                          <Counters>
                            <Counter>
                              <CounterLabel>
                                <span>{copy.roomsLabel}</span>
                              </CounterLabel>
                              <Stepper>
                                <button
                                  type="button"
                                  aria-label={copy.remove}
                                  disabled={!pick}
                                  onClick={() =>
                                    setPick(offer.code, {
                                      rooms: (pick?.rooms || 0) - 1,
                                    })
                                  }
                                >
                                  <Minus size={15} />
                                </button>
                                <output>{pick?.rooms || 0}</output>
                                <button
                                  type="button"
                                  aria-label={copy.add}
                                  onClick={() =>
                                    setPick(offer.code, {
                                      rooms: (pick?.rooms || 0) + 1,
                                    })
                                  }
                                >
                                  <Plus size={15} />
                                </button>
                              </Stepper>
                            </Counter>

                            {pick ? (
                              <>
                                <Counter>
                                  <CounterLabel>
                                    <span>{copy.guests}</span>
                                    <small>{copy.guestsHint}</small>
                                  </CounterLabel>
                                  <Stepper>
                                    <button
                                      type="button"
                                      aria-label={copy.remove}
                                      disabled={pick.guests <= 1}
                                      onClick={() =>
                                        setPick(offer.code, {
                                          guests: pick.guests - 1,
                                        })
                                      }
                                    >
                                      <Minus size={15} />
                                    </button>
                                    <output>{pick.guests}</output>
                                    <button
                                      type="button"
                                      aria-label={copy.add}
                                      disabled={pick.guests >= maxGuests}
                                      onClick={() =>
                                        setPick(offer.code, {
                                          guests: pick.guests + 1,
                                        })
                                      }
                                    >
                                      <Plus size={15} />
                                    </button>
                                  </Stepper>
                                </Counter>

                                <Counter>
                                  <CounterLabel>
                                    <span>{copy.children}</span>
                                    <small>{copy.childrenHint}</small>
                                  </CounterLabel>
                                  <Stepper>
                                    <button
                                      type="button"
                                      aria-label={copy.remove}
                                      disabled={pick.children <= 0}
                                      onClick={() =>
                                        setPick(offer.code, {
                                          children: pick.children - 1,
                                        })
                                      }
                                    >
                                      <Minus size={15} />
                                    </button>
                                    <output>{pick.children}</output>
                                    <button
                                      type="button"
                                      aria-label={copy.add}
                                      onClick={() =>
                                        setPick(offer.code, {
                                          children: pick.children + 1,
                                        })
                                      }
                                    >
                                      <Plus size={15} />
                                    </button>
                                  </Stepper>
                                </Counter>
                              </>
                            ) : null}
                          </Counters>

                          {beds > 0 ? (
                            <BedNote>
                              {beds} × {copy.extraBed} ·{" "}
                              {rupiah(data.extra_bed?.per_night_rupiah || 0)}{" "}
                              {copy.perRoomPerNight}
                            </BedNote>
                          ) : null}
                        </>
                      )}
                    </Rail>
                  </RoomCard>
                );
              })}
            </div>

            <Basket aria-live="polite">
              <BasketTitle>{copy.yourStay}</BasketTitle>
              {lines.length === 0 ? (
                <Empty>{copy.emptyBasket}</Empty>
              ) : (
                <>
                  {lines.map((line) => (
                    <Line key={line.code}>
                      <span>
                        {line.rooms}× {line.name}
                        {line.beds ? ` · ${line.beds} ${copy.extraBed}` : ""}
                      </span>
                      <strong>{rupiah(line.total)}</strong>
                    </Line>
                  ))}
                  <Total>
                    <span>
                      {copy.estimatedTotal}
                      <Asterisk>*</Asterisk>
                    </span>
                    <strong>{rupiah(grandTotal)}</strong>
                  </Total>
                  <Button
                    as="a"
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    $variant="primary"
                    $size="lg"
                  >
                    {copy.bookViaWhatsapp}
                  </Button>
                </>
              )}
              <Note>
                <Info size={14} aria-hidden="true" />
                <span>
                  <Asterisk>*</Asterisk> {copy.priceNote}
                </span>
              </Note>
              <Note>
                <Info size={14} aria-hidden="true" />
                <span>{copy.occupancyNote}</span>
              </Note>

              <Included>
                <h4>{copy.includedTitle}</h4>
                <ul>
                  {[copy.incl1, copy.incl2, copy.incl3, copy.incl4].map(
                    (item) => (
                      <li key={item}>
                        <Check size={15} aria-hidden="true" />
                        {item}
                      </li>
                    ),
                  )}
                </ul>
              </Included>
            </Basket>
          </Layout>
        ) : null}
      </Container>

      {viewing ? (
        <Lightbox
          images={viewing.images}
          initialIndex={viewing.at}
          onClose={() => setViewing(null)}
          label={viewing.name}
          closeLabel={t.gallery.close}
          prevLabel={t.gallery.prev}
          nextLabel={t.gallery.next}
        />
      ) : null}
    </Section>
  );
};

export default BookingPanel;
